import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { toast } from "react-toastify";
import styled from "styled-components";
import { fetchCrewMember, modifyCrewMember, selectCrewMemberById } from "../common/slices/crewMember";
import { updateRateOverride, createRateOverride } from "../common/slices/user";
import { selectActive } from "../common/slices/location";
import { BlackButton, ButtonBase, LightButton } from "./Buttons";
import FormTextInput from "./FormTextInput";
import Modal from "./Modal";
import Row from "./Row";
import { H1, P, P2 } from "./Typography";
import { ErrorContext } from "../ErrorContext";
import FormCheckbox from "./FormCheckbox";

const Attribute = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`

const ModalEditCrewMember = ({ crewMemberId, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const locationId = useSelector(selectActive);
    const crewMember = useSelector(state => selectCrewMemberById(state, crewMemberId));

    useEffect(() => {
        (async () => {
            try {
                if (crewMemberId && locationId) {
                    await dispatch(fetchCrewMember(crewMemberId, locationId));
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to fetch crew member", forSupport: ex.message })
            }
        })()
    }, [crewMemberId, locationId])

    const {
        control,
        formState: { errors, isSubmitting, isValid },
        setValue,
        handleSubmit
    } = useForm({
        values: crewMember,
    })

    useEffect(() => {
        if (crewMember?.rateOverride?.rate) {
            setValue('ro', crewMember?.rateOverride?.rate)
        }
    }, [crewMember])

    const formProps = { control, errors, locationId }

    const onSave = async (data) => {
        try {
            await dispatch(modifyCrewMember(data));
            if (crewMember?.rateOverride) {
                await dispatch(updateRateOverride({ crewMemberId: data.id, locationId: data.locationId, rate: data.ro }))
            } else {
                await dispatch(createRateOverride({ crewMemberId: data.id, locationId: data.locationId, rate: data.ro }))
            }
            onClose();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to save crew member", forSupport: ex.message })
        }
    }

    return (
        <Modal show size="60%" zindex='1200'>
            <Row>
                <H1>Crew Member</H1>
                <ButtonBase onClick={onClose} style={{ marginLeft: 'auto' }}><FaTimes size='16px' color="#000" /></ButtonBase>
            </Row>

            <Row>
                <Attribute>
                    <P2>QuickBooks ID</P2>
                    <P>{crewMember?.id}</P>
                </Attribute>
                <Attribute>
                    <P2>Location</P2>
                    <P>{crewMember?.location?.name}</P>
                </Attribute>
                <Attribute>
                    <P2>Email Address</P2>
                    <P>{crewMember?.email}</P>
                </Attribute>
            </Row>
            <Row>
                <FormTextInput {...formProps} name="name" label="Full Name" placeholder="Enter full name" rules={{ required: true }} />
                <FormTextInput {...formProps} name="rate" label="Rate ($)" placeholder="Enter hourly rate" rules={{
                    required: true, pattern: {
                        value: /^(0|[1-9]\d*)(\.\d+)?$/
                    },
                }} disabled={true} />
                <FormTextInput {...formProps} name="ro" label="Rate Override ($)" placeholder="Enter rate to override calculations" rules={{
                    required: true, pattern: {
                        value: /^(0|[1-9]\d*)(\.\d+)?$/
                    },
                }} />
            </Row>
            <Row>
                <BlackButton onClick={handleSubmit(onSave)} disabled={isSubmitting || !isValid}>Save</BlackButton>
                <FormCheckbox {...formProps} name="active" placeholder="Is Active?" />
                <LightButton onClick={onClose} disabled={isSubmitting}>Cancel</LightButton>
            </Row>
        </Modal>
    );
};

export default ModalEditCrewMember;
